/**
 * Billing service for Stripe integration
 * Handles checkout, subscription management, and usage tracking
 */

import { httpsCallable, HttpsCallableResult } from 'firebase/functions';
import { functions } from './firebase';
import type {
  CheckoutRequest,
  CheckoutResponse,
  UsageResponse,
  PlanType,
  BillingCycle,
} from '@msb/shared';

/**
 * Subscription response from getSubscription function
 */
interface SubscriptionResponse {
  subscription: {
    id: string;
    planType: PlanType;
    billingCycle: BillingCycle;
    status: string;
    currentPeriodStart: string;
    currentPeriodEnd: string;
    cancelAtPeriodEnd: boolean;
    trialEnd?: string;
  } | null;
  portalUrl: string;
}

/**
 * Cancel subscription response
 */
interface CancelResponse {
  success: boolean;
  message: string;
  cancelAtPeriodEnd: boolean;
  periodEnd?: string;
}

/**
 * Portal URL response
 */
interface PortalUrlResponse {
  portalUrl: string;
}

/**
 * Call a Firebase function and extract the data
 */
async function callFunction<TRequest, TResponse>(
  name: string,
  data?: TRequest
): Promise<TResponse> {
  const fn = httpsCallable<TRequest, TResponse>(functions, name);
  const result: HttpsCallableResult<TResponse> = await fn(data as TRequest);
  return result.data;
}

/**
 * Billing service for frontend
 */
export const billingService = {
  /**
   * Create a checkout session for a plan
   * Returns the URL to redirect the user to
   */
  createCheckout: async (
    planType: PlanType,
    billingCycle: BillingCycle,
    discountCode?: string
  ): Promise<CheckoutResponse> => {
    const request: CheckoutRequest = {
      planType,
      billingCycle,
      ...(discountCode ? { discountCode } : {}),
    };

    return callFunction<CheckoutRequest, CheckoutResponse>('createCheckout', request);
  },

  /**
   * Get the current user's subscription
   */
  getSubscription: async (): Promise<SubscriptionResponse> => {
    return callFunction<void, SubscriptionResponse>('getSubscription');
  },

  /**
   * Get the current user's usage for the current month
   */
  getUsage: async (): Promise<UsageResponse> => {
    return callFunction<void, UsageResponse>('getUsage');
  },

  /**
   * Cancel the current user's subscription
   * Sets cancel_at_period_end = true (access continues until period ends)
   */
  cancelSubscription: async (): Promise<CancelResponse> => {
    return callFunction<void, CancelResponse>('cancelSubscription');
  },

  /**
   * Get the customer portal URL
   * Used to redirect users to manage payment methods, invoices, etc.
   */
  getPortalUrl: async (): Promise<string> => {
    const response = await callFunction<void, PortalUrlResponse>('getPortalUrl');
    return response.portalUrl;
  },

  /**
   * Redirect user to checkout
   */
  redirectToCheckout: async (
    planType: PlanType,
    billingCycle: BillingCycle,
    discountCode?: string
  ): Promise<void> => {
    const checkout = await billingService.createCheckout(planType, billingCycle, discountCode);
    window.location.href = checkout.checkoutUrl;
  },

  /**
   * Redirect user to customer portal
   */
  redirectToPortal: async (): Promise<void> => {
    const portalUrl = await billingService.getPortalUrl();
    window.location.href = portalUrl;
  },

  /**
   * Apply a beta code to the current user's account.
   * Called after registration (email or Google) when a beta code is present.
   */
  applyBetaCode: async (code: string): Promise<{ success: boolean; alreadyApplied: boolean }> => {
    return callFunction<{ code: string }, { success: boolean; alreadyApplied: boolean }>(
      'applyBetaCode',
      { code }
    );
  },
};

export default billingService;
